"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Link, useNavigate } from "react-router-dom"
import Nav from "../components/Nav"
import BGLogo from "../assets/BGLogo.png"
import { supabase } from "../lib/supabaseClient"
import { AlertCircle, Shield } from "lucide-react"

const Login = () => {
  const navigate = useNavigate()
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState(null)

  // Check if user is already logged in
  const checkUser = async () => {
    const { data } = await supabase.auth.getSession()
    if (data.session) {
      // Check if email is in admin_access table
      const { data: adminData, error: adminError } = await supabase
        .from("admin_access")
        .select("*")
        .eq("email", data.session.user.email)
        .eq("is_active", true)
        .single()

      if (adminData) {
        // Update last login time
        await supabase
          .from("admin_access")
          .update({ last_login: new Date().toISOString() })
          .eq("email", data.session.user.email)

        // Use navigate to go to dashboard
        navigate("/dashboard")
      } else {
        // Not authorized, sign out and stay on login page
        await supabase.auth.signOut()
        setError("You don't have permission to access the admin area.")
      }
    }
  }
  useEffect(() => {
    checkUser()
  }, [navigate])

  // Handle Google login
  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true)
      setError(null)

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
          redirectTo: window.location.origin + "/dashboard",
        },
      })

      if (error) {
        throw error
      }
    } catch (error) {
      console.error("Error logging in with Google:", error)
      setError(error.message || "Failed to log in with Google")
    } finally {
      setGoogleLoading(false)
    }
  }

  // Animated background particles
  const particles = Array.from({ length: 15 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 8 + 4,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5,
  }))

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-indigo-900 to-indigo-950 text-white overflow-hidden flex flex-col">
      <Nav />

      <div className="fixed inset-0 z-0 opacity-20">
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 8,
            repeat: Number.POSITIVE_INFINITY,
            repeatType: "reverse",
          }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-600 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{
            duration: 10,
            repeat: Number.POSITIVE_INFINITY,
            repeatType: "reverse",
          }}
        />
      </div>

      {/* Main content - centered */}
      <div className="flex-1 flex items-center justify-center px-4 relative z-10">
        <div className="w-full max-w-md">
          {/* Logo with glow */}
          <motion.div
            className="flex justify-center mb-12"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div whileHover={{ scale: 1.08, rotate: 3 }} className="relative">
              <motion.div
                className="absolute inset-0 bg-yellow-400 rounded-full blur-2xl"
                animate={{
                  scale: [1, 1.15, 1],
                  opacity: [0.4, 0.7, 0.4],
                }}
                transition={{
                  duration: 3,
                  repeat: Number.POSITIVE_INFINITY,
                  repeatType: "reverse",
                }}
              />
              <img
                src={BGLogo || "/placeholder.svg"}
                alt="Exodus Music Ministry"
                className="h-28 w-auto relative z-10"
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="bg-indigo-900/40 backdrop-blur-xl rounded-3xl p-8 border border-indigo-700/50 shadow-2xl"
          >
            {/* Header section */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-center mb-8"
            >
              <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text text-transparent">
                Admin Access
              </h1>
              <p className="text-indigo-200 text-lg">Sign in with your Google account</p>
            </motion.div>

            {/* Error message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-500/15 border border-red-500/40 rounded-xl text-red-200 flex items-start gap-3 backdrop-blur-sm"
              >
                <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </motion.div>
            )}

            {/* Security badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-center gap-2 mb-8 text-indigo-300 text-sm"
            >
              <Shield className="h-4 w-4" />
              <span>Secure authentication powered by Google</span>
            </motion.div>

            <motion.button
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              className="relative w-full bg-white text-gray-900 font-semibold rounded-2xl py-4 px-6 flex items-center justify-center gap-3 overflow-hidden group shadow-lg hover:shadow-xl transition-shadow"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Shimmer effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-yellow-400/0 via-yellow-300/20 to-yellow-400/0 z-0"
                animate={{
                  x: ["-100%", "100%"],
                }}
                transition={{
                  duration: 2,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "linear",
                }}
              />

              {!googleLoading ? (
                <>
                  <svg className="w-5 h-5 relative z-10" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="relative z-10 text-lg">Sign in with Google</span>
                </>
              ) : (
                <div className="flex items-center justify-center relative z-10">
                  <motion.div
                    className="w-5 h-5 border-2 border-gray-400 border-t-gray-900 rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                  />
                  <span className="ml-3">Connecting...</span>
                </div>
              )}
            </motion.button>

            {/* Security info */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-8 p-4 bg-indigo-800/30 rounded-xl border border-indigo-700/30"
            >
              <p className="text-indigo-200 text-sm text-center">
                Only authorized administrators have access to the dashboard.
              </p>
            </motion.div>

            {/* Back to home link */}
            <motion.div
              className="mt-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <Link to="/" className="text-indigo-300 hover:text-yellow-400 transition-colors text-sm font-medium">
                Back to home
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Decorative floating elements */}
      <motion.div
        className="absolute top-20 right-10 w-32 h-32 bg-yellow-400/5 rounded-full blur-2xl"
        animate={{
          y: [0, 20, 0],
          x: [0, 10, 0],
        }}
        transition={{
          duration: 6,
          repeat: Number.POSITIVE_INFINITY,
          repeatType: "reverse",
        }}
      />
      <motion.div
        className="absolute bottom-32 left-10 w-40 h-40 bg-indigo-600/5 rounded-full blur-3xl"
        animate={{
          y: [0, -20, 0],
          x: [0, -10, 0],
        }}
        transition={{
          duration: 8,
          repeat: Number.POSITIVE_INFINITY,
          repeatType: "reverse",
        }}
      />
    </div>
  )
}

export default Login
